import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/person.pb';
import { AdaptoActions } from '@frontend/adapto-actions';
import { generateTrackingId } from '@frontend/call-intel-core';
import { Actions } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { removeNonDigits } from '@frontend/phone-numbers';
import { Photos } from '@frontend/photos';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

type Contact = Required<Pick<Person, 'firstName' | 'lastName' | 'personId'>> & {
  profileName: string;
  number: string;
  defaultName: string;
};

type ContactActionsProps = {
  contact: Contact;
  locationId: string;
};

type CallInsightContactSectionProps = {
  callDirection: string;
} & ContactActionsProps;

const ContactActions = ({ contact, locationId }: ContactActionsProps) => {
  const { t } = useTranslation('call-insight-panel');

  return (
    <section css={styles.actionsSection}>
      <AdaptoActions enableCloseEvent iconColor='inherit' variant='icon-buttons' css={{ display: 'flex' }}>
        <Actions.Call
          label={t(`Call ${contact.profileName}`)}
          context={{ personId: contact.personId, phoneNumber: removeNonDigits(contact.number), locationId }}
          trackingIdSuffix={generateTrackingId({ prefix: 'call-insight-panel', context: 'call-contact' })}
        />
        <Actions.Message
          label={t(`Text ${contact.profileName}`)}
          context={{
            personId: contact.personId,
            personPhone: removeNonDigits(contact.number),
            threadGroupId: locationId,
          }}
          trackingIdSuffix={generateTrackingId({ prefix: 'call-insight-panel', context: 'message-contact' })}
        />
      </AdaptoActions>
    </section>
  );
};

export const CallInsightContactSection = ({ contact, locationId, callDirection }: CallInsightContactSectionProps) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  const { firstName, lastName, profileName, personId, number, defaultName } = contact;
  const isOutboundCall = callDirection === 'outbound';

  return (
    <div css={styles.wrapper}>
      <div css={styles.row}>
        <Photos.ContactProfilePhoto
          css={styles.avatar}
          disableClick={!personId}
          locationId={locationId}
          name={profileName || defaultName}
          personId={personId}
        />

        <div css={styles.infoStack}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {firstName || lastName ? (
              <div
                css={styles.name}
                onClick={() => {
                  if (!!personId) {
                    setPersonId(personId, undefined, locationId);
                    setShow(true, 'contact');
                  }
                }}
              >
                {!!firstName && (
                  <Text as='span' weight='bold'>
                    {firstName}
                  </Text>
                )}
                {!!lastName && <Text as='span'> {lastName}</Text>}
              </div>
            ) : (
              <Text as='span' color='light'>
                {number}
              </Text>
            )}
            {isOutboundCall && <Icon name='phone-outgoing-small' css={{ marginLeft: theme.spacing(1) }} />}
          </div>

          {(firstName || lastName) && (
            <Text size='small' color='light'>
              {number}
            </Text>
          )}
        </div>
      </div>
      <ContactActions contact={contact} locationId={locationId} />
    </div>
  );
};

const styles = {
  wrapper: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  row: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  avatar: css({
    flexShrink: 0,
  }),
  infoStack: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  }),
  name: css({
    cursor: 'pointer',
    width: 'fit-content',
  }),
  actionsSection: css`
    display: flex;
    justify-content: center;
    gap: ${theme.spacing(1)};
  `,
  adaptoActions: css({
    display: 'flex',
  }),
};
